import React, { useState, useEffect, useCallback } from "react";
import {
  formatDateTime,
  getRelativeTime,
  formatPortalName,
  extractTotalClicks,
  extractPortalsList,
  extractLatestUpdated,
  extractTopPortal,
  buildFullApiUrl,
} from "./utils";

import WebAnalyticsTable from "./WebAnalyticsTable";
import "./WebAnalytics.css";
import axios from "axios";

function WebAnalytics() {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchPortalClicks = useCallback(async (targetPortalId) => {
    setLoading(true);
    setError("");

    try {
      const requestUrl = buildFullApiUrl(targetPortalId);
      const response = await axios.get(requestUrl);

      if (response && response.data) {
        setAnalyticsData(response.data);
      } else {
        setAnalyticsData(null);
        setError("No response data received from server.");
      }
    } catch (err) {
      console.error("Error fetching portal click analytics:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch portal click analytics.";
      setError(message);
    } finally {
      setLoading(false);
      setLastRefreshedAt(new Date());
    }
  }, []);

  useEffect(() => {
    fetchPortalClicks();
  }, [fetchPortalClicks]);

  const totalClicks = extractTotalClicks(analyticsData);
  const portalsList = extractPortalsList(analyticsData);
  const latestUpdated = extractLatestUpdated(analyticsData);
  const topPortal = extractTopPortal(analyticsData);

  const kpiCards = [
    {
      label: "Total Click Count",
      value: totalClicks.toLocaleString(),
      subtext: "Accumulated portal clicks",
      gradientClass: "wa-gradient-blue",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 0 1-6 6M6 8a6 6 0 0 0 6 6" />
          <path d="M12 14v4" />
          <path d="M8 18h8" />
          <circle cx="12" cy="4" r="2" />
        </svg>
      ),
    },
    {
      label: "Active Portals",
      value: portalsList.length,
      subtext: "Tracked tourist portals",
      gradientClass: "wa-gradient-purple",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
        </svg>
      ),
    },
    {
      label: "Top Portal",
      value: topPortal ? formatPortalName(topPortal.portalId) : "N/A",
      subtext: topPortal ? `${Number(topPortal.count).toLocaleString()} clicks recorded` : "No data available",
      gradientClass: "wa-gradient-orange",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ),
    },
    {
      label: "Last Updated",
      value: formatDateTime(latestUpdated),
      subtext: getRelativeTime(latestUpdated) || "N/A",
      gradientClass: "wa-gradient-green",
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
    },
  ];

  return (
    <div className="container-fluid px-4 py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
        <div>
          <h2 className="fw-bold mb-0 wa-header-title">Portal Clicks Analytics</h2>
        </div>

        <div className="d-flex align-items-center gap-2">
          {lastRefreshedAt && (
            <span className="text-muted small">
              <i className="bi bi-clock me-1"></i>{getRelativeTime(lastRefreshedAt)}
            </span>
          )}
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 wa-header-refresh"
            onClick={() => fetchPortalClicks()}
            disabled={loading}
          >
            <i className={`bi bi-arrow-clockwise ${loading ? "wa-spin" : ""}`}></i>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="row g-4 mb-4">
        {kpiCards.map((card, idx) => (
          <div key={idx} className="col-12 col-sm-6 col-xl-3">
            <div className={`card h-100 wa-kpi-card shadow ${card.gradientClass}`}>
              <div className="wa-kpi-blob"></div>
              <div className="wa-kpi-blob-sm"></div>
              <div className="wa-kpi-inner text-white">
                <div>
                  <div className="wa-kpi-label">{card.label}</div>
                  <div className="wa-kpi-value">{card.value}</div>
                  <div className="wa-kpi-subtext">{card.subtext}</div>
                </div>
                <div className="wa-kpi-icon-box">
                  {card.icon}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="alert alert-warning border-0 shadow-sm d-flex align-items-center justify-content-between mb-4" role="alert">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill fs-5 text-warning"></i>
            <span>{error}</span>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      <WebAnalyticsTable
        data={analyticsData}
        loading={loading}
        onRefresh={() => fetchPortalClicks()}
      />
    </div>
  );
}

export default WebAnalytics;

